import React, { useEffect, useRef, useState } from "react";
import { Modal, Box, Typography, Button, IconButton, Tooltip } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  C2_FONT,
  c2DialogPaperSx,
  c2IconBoxSx,
  c2PrimaryBtnSx,
  c2SecondaryBtnSx,
} from "./conecta2ModalStyles";

const C2 = {
  sidebar: "#1A0F3B",
  dark: "#221448",
  violet: "#6411ED",
  white: "#FBFAFE",
  lavender: "#EBE7FB",
  border: "#D7CFEB",
  muted: "#766293",
};

const ACCEPTED_TYPES = ["image/png", "image/webp", "image/jpeg"];
const TRANSPARENT_TYPES = ["image/png", "image/webp"];
const RECOMMENDED = { width: 300, height: 200, maxKb: 50, warnKb: 200 };
const MIN_SIZE = { width: 216, height: 120 };

const checkerboardSx = {
  backgroundColor: "#FFFFFF",
  backgroundImage:
    "linear-gradient(45deg, #EEEAF8 25%, transparent 25%), linear-gradient(-45deg, #EEEAF8 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #EEEAF8 75%), linear-gradient(-45deg, transparent 75%, #EEEAF8 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
};

function formatKb(bytes) {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.round(kb)} KB`;
}

function PreviewFrame({ label, src, highlight }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.75rem", fontWeight: 700, color: C2.muted, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </Typography>
      <Box
        sx={{
          ...checkerboardSx,
          aspectRatio: "3 / 2",
          borderRadius: "12px",
          border: `${highlight ? 2 : 1}px solid ${highlight ? C2.violet : C2.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          p: 1,
        }}
      >
        {src ? (
          <img alt={label} src={src} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        ) : (
          <ImageOutlinedIcon sx={{ fontSize: 36, color: C2.border }} />
        )}
      </Box>
    </Box>
  );
}

function AppCardPreview({ src, dark }) {
  return (
    <Box
      sx={{
        width: 92,
        py: 1,
        borderRadius: "14px",
        backgroundColor: dark ? C2.dark : "#FFFFFF",
        border: `1px solid ${dark ? "#3B2275" : C2.border}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        boxShadow: "0 2px 6px rgba(26,15,59,0.12)",
      }}
    >
      <Box sx={{ width: 72, height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {src ? <img alt="" src={src} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} /> : null}
      </Box>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.68rem", fontWeight: 700, color: dark ? C2.white : C2.sidebar }}>
        {dark ? "Modo oscuro" : "Modo claro"}
      </Typography>
    </Box>
  );
}

function CheckRow({ ok, children }) {
  const Icon = ok ? CheckCircleRoundedIcon : WarningAmberRoundedIcon;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
      <Icon sx={{ fontSize: 16, mt: "2px", color: ok ? "#16A34A" : "#D97706" }} />
      <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.8rem", color: C2.dark, lineHeight: 1.4 }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function CarTypeImageModal({
  open,
  onClose,
  onSave,
  container,
  isRTL,
  title,
  carTypeName,
  currentImage,
  selectedImage,
  onSelectImage,
}) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [dimensions, setDimensions] = useState(null);
  const [typeError, setTypeError] = useState("");

  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!selectedImage) {
      setPreviewUrl(null);
      setDimensions(null);
      return undefined;
    }
    const url = URL.createObjectURL(selectedImage);
    setPreviewUrl(url);
    setDimensions(null);
    const img = new window.Image();
    img.onload = () => setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [selectedImage]);

  useEffect(() => {
    if (!open) {
      setTypeError("");
      setDragOver(false);
    }
  }, [open]);

  const pickFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setTypeError("Formato no válido. Usa PNG, WebP o JPG.");
      return;
    }
    setTypeError("");
    onSelectImage(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer?.files?.[0]);
  };

  const clearFile = () => {
    onSelectImage(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const sizeKb = selectedImage ? selectedImage.size / 1024 : 0;
  const ratio = dimensions ? dimensions.width / dimensions.height : null;
  const checks = selectedImage
    ? [
        {
          ok: TRANSPARENT_TYPES.includes(selectedImage.type),
          text: TRANSPARENT_TYPES.includes(selectedImage.type)
            ? "Formato con transparencia."
            : "JPG no admite fondo transparente; se verá un recuadro en modo oscuro.",
        },
        dimensions && {
          ok: dimensions.width >= MIN_SIZE.width && dimensions.height >= MIN_SIZE.height,
          text:
            dimensions.width >= MIN_SIZE.width && dimensions.height >= MIN_SIZE.height
              ? `${dimensions.width} × ${dimensions.height} px.`
              : `${dimensions.width} × ${dimensions.height} px: muy pequeña, puede verse borrosa.`,
        },
        ratio && {
          ok: ratio >= 1.3 && ratio <= 1.9,
          text:
            ratio >= 1.3 && ratio <= 1.9
              ? "Proporción horizontal adecuada."
              : "Proporción muy distinta a 3:2; el vehículo se verá pequeño o con márgenes.",
        },
        {
          ok: sizeKb <= RECOMMENDED.warnKb,
          text:
            sizeKb <= RECOMMENDED.maxKb
              ? `Peso ${formatKb(selectedImage.size)}.`
              : sizeKb <= RECOMMENDED.warnKb
              ? `Peso ${formatKb(selectedImage.size)}; aceptable, ideal menos de ${RECOMMENDED.maxKb} KB.`
              : `Peso ${formatKb(selectedImage.size)}: muy pesada, hará lento el mapa en los celulares.`,
        },
      ].filter(Boolean)
    : [];

  const shownImage = previewUrl || currentImage || null;

  return (
    <Modal
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      open={open}
      onClose={onClose}
      container={container}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          ...c2DialogPaperSx,
          outline: "none",
          maxWidth: 560,
          width: "calc(100% - 32px)",
          maxHeight: "92vh",
          overflowY: "auto",
          direction: isRTL === "rtl" ? "rtl" : "ltr",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: 1.5 }}>
          <Box sx={c2IconBoxSx}>
            <CloudUploadOutlinedIcon sx={{ color: C2.violet }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: "1.1rem", color: C2.sidebar, lineHeight: 1.2 }}>
              {title}
            </Typography>
            {carTypeName ? (
              <Typography noWrap sx={{ fontFamily: C2_FONT, fontSize: "0.85rem", color: C2.muted }}>
                {carTypeName}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: C2.muted }} aria-label="Cerrar">
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <PreviewFrame label="Actual" src={currentImage} />
            <PreviewFrame label="Nueva" src={previewUrl} highlight={!!previewUrl} />
          </Box>

          {selectedImage ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.25,
                borderRadius: "12px",
                border: `1px solid ${C2.border}`,
                backgroundColor: "#FFFFFF",
              }}
            >
              <ImageOutlinedIcon sx={{ color: C2.violet }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: "0.88rem", color: C2.sidebar }}>
                  {selectedImage.name}
                </Typography>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.76rem", color: C2.muted }}>
                  {formatKb(selectedImage.size)}
                  {dimensions ? ` · ${dimensions.width} × ${dimensions.height} px` : ""}
                </Typography>
              </Box>
              <Tooltip title="Quitar archivo">
                <IconButton onClick={clearFile} size="small" sx={{ color: C2.muted, "&:hover": { color: "#B91C1C" } }}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box
              component="label"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                py: 2.5,
                px: 2,
                borderRadius: "14px",
                border: `2px dashed ${dragOver ? C2.violet : C2.border}`,
                backgroundColor: dragOver ? C2.lavender : "#FFFFFF",
                cursor: "pointer",
                textAlign: "center",
                transition: "border-color 0.2s, background-color 0.2s",
                "&:hover": { borderColor: C2.violet, backgroundColor: C2.lavender },
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: C2.violet }} />
              <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: "0.92rem", color: C2.sidebar }}>
                Arrastra la imagen aquí o haz clic para elegirla
              </Typography>
              <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.78rem", color: C2.muted }}>
                PNG transparente · {RECOMMENDED.width} × {RECOMMENDED.height} px (3:2) · menos de {RECOMMENDED.maxKb} KB
              </Typography>
              <input
                ref={inputRef}
                type="file"
                hidden
                accept={ACCEPTED_TYPES.join(",")}
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            </Box>
          )}

          {typeError ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.82rem", color: "#B91C1C" }}>{typeError}</Typography>
          ) : null}

          {checks.length ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {checks.map((c) => (
                <CheckRow key={c.text} ok={c.ok}>{c.text}</CheckRow>
              ))}
            </Box>
          ) : null}

          {shownImage ? (
            <Box>
              <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.75rem", fontWeight: 700, color: C2.muted, mb: 0.75, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Así se verá en la app
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <AppCardPreview src={shownImage} />
                <AppCardPreview src={shownImage} dark />
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            flexWrap: "wrap",
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: 1,
            borderTop: `1px solid ${C2.lavender}`,
          }}
        >
          <Button onClick={onClose} variant="outlined" sx={c2SecondaryBtnSx}>
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            variant="contained"
            disabled={!selectedImage}
            startIcon={<CloudUploadOutlinedIcon />}
            sx={{
              ...c2PrimaryBtnSx,
              "&.Mui-disabled": { background: C2.lavender, color: C2.muted, boxShadow: "none" },
            }}
          >
            Subir imagen
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
